// src/pages/shop/ShopPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import BookGrid from '../../components/book/BookGrid';
import BookFilters from '../../components/book/BookFilters';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ShopPage = () => {
  const [displayedBooks, setDisplayedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBrowse, setActiveBrowse] = useState('all');

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [priceRange, setPriceRange] = useState([0, 100]);
  const [sortBy, setSortBy] = useState('recommended');

  useEffect(() => {
    const fetchBooks = async () => {
      setLoading(true);
      try {
        let snap;

        if (activeBrowse === 'featured') {
          snap = await getDocs(collection(db, 'featuredBooks'));
        } else if (activeBrowse === 'bestsellers') {
          snap = await getDocs(collection(db, 'bestSellers'));
        } else if (activeBrowse === 'new') {
          snap = await getDocs(collection(db, 'newArrivals'));
        } else {
          snap = await getDocs(collection(db, 'books'));
        }

        const refs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        if (activeBrowse === 'all') {
          setDisplayedBooks(refs);
        } else {
          const bookIds = refs.map(ref => ref.bookId).filter(Boolean);

          if (!bookIds.length) {
            setDisplayedBooks([]);
            return;
          }

          const booksSnap = await getDocs(collection(db, 'books'));
          const booksMap = {};
          booksSnap.docs.forEach(doc => {
            booksMap[doc.id] = { id: doc.id, ...doc.data() };
          });

          setDisplayedBooks(bookIds.map(id => booksMap[id]).filter(Boolean));
        }
      } catch (err) {
        console.error('Error fetching books:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBooks();
  }, [activeBrowse]);

  /* ✅ DYNAMIC GENRES FROM BACKEND */
  const genres = useMemo(() => {
    const set = new Set();
    displayedBooks.forEach(book => {
      if (book.genre) set.add(book.genre);
    });
    return ['All Genres', ...Array.from(set).sort()];
  }, [displayedBooks]);

  /* Filters */
  const filteredAndSortedBooks = displayedBooks
    .filter(book => {
      const matchesSearch =
        book.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        book.author?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesGenre =
        !selectedGenre ||
        selectedGenre === 'All Genres' ||
        book.genre === selectedGenre;

      const matchesPrice =
        book.price >= priceRange[0] && book.price <= priceRange[1];

      return matchesSearch && matchesGenre && matchesPrice;
    })
    .sort((a, b) => {
      if (sortBy === 'price-low') return a.price - b.price;
      if (sortBy === 'price-high') return b.price - a.price;
      if (sortBy === 'title') return a.title.localeCompare(b.title);
      if (sortBy === 'newest') return (b.addedAt || 0) - (a.addedAt || 0);
      return 0;
    });

  const currentTitle =
    activeBrowse === 'all'
      ? 'All Books'
      : activeBrowse === 'featured'
      ? 'Featured Books'
      : activeBrowse === 'bestsellers'
      ? 'Best Sellers'
      : 'New Arrivals';

  if (loading) return <LoadingSpinner />;

  return (
    <div className="shop-page">
      <div className="shop-breadcrumb">
        <Link to="/" className="breadcrumb-link">Home</Link>
        <span className="breadcrumb-separator">›</span>
        <span>{currentTitle}</span>
      </div>

      <h1 className="shop-title">{currentTitle}</h1>

      {/* Top Bar */}
      <div className="shop-top-bar">
        <div className="browse-and-count">
          <div className="browse-by-bar">
            <span className="browse-label">Browse by:</span>
            {['all', 'featured', 'bestsellers', 'new'].map(type => (
              <button
                key={type}
                className={`browse-btn ${activeBrowse === type ? 'active' : ''}`}
                onClick={() => setActiveBrowse(type)}
              >
                {type === 'all'
                  ? 'All Books'
                  : type === 'featured'
                  ? 'Featured'
                  : type === 'bestsellers'
                  ? 'Best Sellers'
                  : 'New Arrivals'}
              </button>
            ))}
          </div>

          <p className="result-count">
            {filteredAndSortedBooks.length} book
            {filteredAndSortedBooks.length !== 1 ? 's' : ''} found
          </p>
        </div>

        <div className="sort-wrapper">
          <label className="sort-label">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="recommended">Recommended</option>
            <option value="price-low">Price: Low → High</option>
            <option value="price-high">Price: High → Low</option>
            <option value="title">Title A–Z</option>
            <option value="newest">Newest First</option>
          </select>
        </div>
      </div>

      <div className="shop-layout">
        <aside className="shop-sidebar">
          <div className="filter-section">
            <h3 className="filter-title">Filter by</h3>
            <BookFilters
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              genres={genres}
              selectedGenre={selectedGenre}
              setSelectedGenre={setSelectedGenre}
              priceRange={priceRange}
              setPriceRange={setPriceRange}
              sortBy={sortBy}
              setSortBy={setSortBy}
            />
          </div>
        </aside>

        <div className="shop-main">
          {filteredAndSortedBooks.length ? (
            <BookGrid books={filteredAndSortedBooks} />
          ) : (
            <div className="shop-empty">
              <p>No books match your filters.</p>
              <button
                className="reset-filters-btn"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedGenre('');
                  setPriceRange([0, 100]);
                  setSortBy('recommended');
                }}
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopPage;
